import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Target, ArrowLeft, Mail, Lock } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

export default function Login() {
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = location.state?.next || "/";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 transition-colors font-mono-digit"
          data-testid="back-to-app"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Hit Path
        </Link>

        <Card className="bg-[#131c2e] border-slate-800 shadow-2xl shadow-black/50">
          <CardContent className="p-6 sm:p-8 space-y-5">
            <div className="flex items-center gap-2 text-amber-400 font-mono-digit text-xs uppercase tracking-[0.3em]">
              <Target className="w-3.5 h-3.5" />
              Hit Path
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-50">
              {mode === "login" ? "Sign in" : "Create your account"}
            </h1>
            <p className="text-sm text-slate-400">
              {mode === "login"
                ? "Pick up today's workout and your saved history."
                : "Save seeds, star picks, and build your win diary."}
            </p>

            <Button
              type="button"
              variant="outline"
              onClick={google}
              data-testid="google-signin-btn"
              className="w-full h-12 bg-white hover:bg-slate-100 text-slate-900 font-heading font-bold gap-2 border-0"
            >
              <svg className="w-5 h-5" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              </svg>
              Continue with Google
            </Button>

            <div className="relative text-center">
              <span className="relative z-10 bg-[#131c2e] px-3 text-xs text-slate-500 uppercase tracking-widest">
                or
              </span>
              <div className="absolute inset-x-0 top-1/2 border-t border-slate-800" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-xs uppercase tracking-widest text-slate-400">
                    Name
                  </Label>
                  <Input
                    id="name"
                    data-testid="auth-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="How should we greet you?"
                    className="h-11 bg-[#0b0f17] border-slate-700"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-400">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    id="email"
                    data-testid="auth-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 pl-9 bg-[#0b0f17] border-slate-700"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs uppercase tracking-widest text-slate-400">
                  Password {mode === "register" && <span className="text-slate-600">(min 8)</span>}
                </Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    id="password"
                    data-testid="auth-password-input"
                    type="password"
                    required
                    minLength={mode === "register" ? 8 : undefined}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 pl-9 bg-[#0b0f17] border-slate-700"
                  />
                </div>
              </div>

              {error && (
                <div
                  data-testid="auth-error"
                  className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/40 rounded-lg px-3 py-2"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                data-testid="auth-submit-btn"
                disabled={busy}
                className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-950 font-heading font-bold"
              >
                {busy ? "…" : mode === "login" ? "Sign in" : "Create account"}
              </Button>
            </form>

            <button
              type="button"
              data-testid="toggle-auth-mode"
              onClick={() => setMode((m) => (m === "login" ? "register" : "login"))}
              className="w-full text-sm text-slate-400 hover:text-amber-300 transition-colors"
            >
              {mode === "login" ? "No account yet? Create one" : "Already have an account? Sign in"}
            </button>
          </CardContent>
        </Card>

        <p className="text-center text-[11px] text-slate-600 leading-relaxed max-w-sm mx-auto">
          By signing in you agree to the <Link to="/terms" className="underline hover:text-slate-400">Terms</Link> and
          {" "}<Link to="/privacy" className="underline hover:text-slate-400">Privacy</Link>. 18+ only. For
          entertainment only — no outcomes guaranteed.
        </p>
      </div>
    </div>
  );
}
